import { useEffect } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/services/firebase/config'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import type { UserProfile, CreateUserData } from '@/types/user'

// Flag to prevent safety-net doc creation from racing with signup flow
let signupInProgress = false

// Hook to initialize auth state listener (call once in App)
export function useAuthInit() {
  const { setUser, setProfile, setLoading } = useAuthStore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser)

      if (firebaseUser) {
        // Load user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          if (userDoc.exists()) {
            const profileData = userDoc.data() as UserProfile
            setProfile(profileData)

            // Sync Firestore email if Auth email changed (e.g. after verifyBeforeUpdateEmail)
            if (firebaseUser.email && profileData.email !== firebaseUser.email) {
              const { updateDoc, serverTimestamp } = await import('firebase/firestore')
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                email: firebaseUser.email,
                updatedAt: serverTimestamp(),
              })
              setProfile({ ...profileData, email: firebaseUser.email })
            }
          } else if (firebaseUser.email && !signupInProgress) {
            // Safety net: Auth account exists but Firestore document is missing
            // (can happen if signup Firestore write failed due to network issues)
            // Skipped during active signup to avoid racing with the signup flow
            const fallbackProfile: Record<string, unknown> = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || 'User',
              location: '',
              accountType: 'buyer',
              subscriptionTier: 'general',
              role: 'user',
              verified: false,
              listingCount: 0,
              salesCount: 0,
              rating: 0,
              reviewCount: 0,
              banned: false,
              showEmail: false,
              showPhone: false,
              enableChat: true,
              enableEnquiryForm: false,
              bio: '',
              phone: '',
              monthlyListingCount: 0,
              createdAt: serverTimestamp(),
            }
            await setDoc(doc(db, 'users', firebaseUser.uid), fallbackProfile)
            setProfile(fallbackProfile as unknown as UserProfile)
            console.warn('Created missing Firestore profile for user:', firebaseUser.uid)
          }
        } catch (error) {
          console.error('Error loading user profile:', error)
        }
      } else {
        setProfile(null)
      }

      setLoading(false)
    })

    return unsubscribe
  }, [setUser, setProfile, setLoading])
}

// Hook for auth actions
export function useAuth() {
  const { user, profile, isLoading, isAuthenticated, logout: clearAuth } = useAuthStore()
  const { showToast, closeModal } = useUIStore()

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password)
      closeModal()
      showToast('Welcome back!')
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string }
      const messages: Record<string, string> = {
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Incorrect password',
        'auth/invalid-email': 'Please enter a valid email address',
        'auth/invalid-credential': 'Invalid email or password',
      }
      showToast(messages[firebaseError.code || ''] || 'Login failed. Please try again.', 'error')
      throw error
    }
  }

  const signup = async (
    data: Omit<CreateUserData, 'subscriptionTier'> & { password: string },
    tier?: 'general' | 'nonprofit',
    nonprofitInfo?: { charityNumber?: string; schoolAddress?: string },
  ) => {
    try {
      // Prevent safety-net from racing with signup
      signupInProgress = true

      // Create Firebase auth user
      const { user: newUser } = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      )

      // Update display name
      await updateProfile(newUser, { displayName: data.displayName })

      // Create user profile in Firestore
      // Nonprofit gets tier immediately (pending admin verification)
      // Paid tiers (storefront/dealership) start as general, activated via Stripe webhook
      const initialTier = tier === 'nonprofit' ? 'nonprofit' : 'general'

      const userProfile: Record<string, unknown> = {
        uid: newUser.uid,
        email: data.email,
        displayName: data.displayName,
        location: data.location,
        accountType: data.accountType,
        subscriptionTier: initialTier,
        role: 'user',
        verified: false,
        listingCount: 0,
        salesCount: 0,
        rating: 0,
        reviewCount: 0,
        createdAt: serverTimestamp(),
      }

      // Store nonprofit verification details for admin review
      if (initialTier === 'nonprofit' && nonprofitInfo) {
        if (nonprofitInfo.charityNumber) userProfile.charityNumber = nonprofitInfo.charityNumber
        if (nonprofitInfo.schoolAddress) userProfile.schoolAddress = nonprofitInfo.schoolAddress
        userProfile.nonprofitVerified = false // pending admin verification
      }

      await setDoc(doc(db, 'users', newUser.uid), userProfile)
      signupInProgress = false

      showToast('Welcome to iSQROLL!')
    } catch (error: unknown) {
      signupInProgress = false
      const firebaseError = error as { code?: string; message?: string }
      const messages: Record<string, string> = {
        'auth/email-already-in-use': 'An account with this email already exists',
        'auth/weak-password': 'Password must be at least 6 characters',
        'auth/invalid-email': 'Please enter a valid email address',
      }
      showToast(messages[firebaseError.code || ''] || 'Signup failed. Please try again.', 'error')
      throw error
    }
  }

  const logout = async () => {
    await signOut(auth)
    clearAuth()
    showToast('Logged out successfully')
  }

  const sendVerification = async () => {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser)
        showToast('Verification email sent! Check your inbox.')
      } catch {
        showToast('Could not send verification email. Try again later.', 'error')
      }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email)
      showToast('Password reset email sent! Check your inbox.')
    } catch (error: unknown) {
      const firebaseError = error as { code?: string }
      if (firebaseError.code === 'auth/user-not-found') {
        showToast('No account found with this email', 'error')
      } else {
        showToast('Could not send reset email. Try again later.', 'error')
      }
      throw error
    }
  }

  return {
    user,
    profile,
    isLoading,
    isAuthenticated,
    emailVerified: user?.emailVerified ?? false,
    login,
    signup,
    logout,
    sendVerification,
    resetPassword,
  }
}
